import {
	ArrowRight,
	Boxes,
	Building2,
	ListTree,
	type LucideIcon,
} from "lucide-react";
import { Link, useParams } from "react-router";

import {
	Card,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	type PermissionInput,
	usePermissions,
} from "@/hooks/use-permissions.ts";

interface SettingsCard {
	segment: string;
	title: string;
	description: string;
	icon: LucideIcon;
	permission?: PermissionInput;
	adminOnly?: boolean;
}

interface SettingsSection {
	title: string;
	description: string;
	cards: SettingsCard[];
}

const sections: SettingsSection[] = [
	{
		title: "Conta",
		description: "Ajustes reutilizáveis em todas as obras da conta.",
		cards: [
			{
				segment: "templates-de-etapas",
				title: "Templates de Etapas",
				description:
					"Modelos de etapas reutilizáveis em qualquer obra da conta.",
				icon: ListTree,
				permission: "stage_templates:list",
			},
			{
				segment: "canteiros",
				title: "Canteiros",
				description: "Nome, endereço, área e datas dos canteiros da conta.",
				icon: Boxes,
				permission: "worksites:list",
			},
			{
				segment: "conta",
				title: "Conta",
				description:
					"Logo impresso no cabeçalho dos Relatórios de Obra da conta.",
				icon: Building2,
				adminOnly: true,
			},
		],
	},
];

export function ConfiguracoesPage() {
	const { obra } = useParams();
	const { has, isAdmin, isLoading } = usePermissions();

	if (isLoading) {
		return (
			<div className="settings-page">
				<div>
					<h1 className="page-title">Configurações</h1>
					<p className="page-subtitle">Gerencie os ajustes da conta.</p>
				</div>
				<section className="settings-page__section">
					<div className="settings-page__skeleton-heading">
						<Skeleton className="settings-page__skeleton-section-title" />
						<Skeleton className="settings-page__skeleton-section-subtitle" />
					</div>
					<div className="card-grid">
						<SettingsCardSkeleton />
						<SettingsCardSkeleton />
						<SettingsCardSkeleton />
					</div>
				</section>
			</div>
		);
	}

	const visibleSections = sections
		.map((section) => ({
			...section,
			cards: section.cards.filter(
				(c) =>
					(!c.permission || has(c.permission)) && (!c.adminOnly || isAdmin),
			),
		}))
		.filter((section) => section.cards.length > 0);

	return (
		<div className="settings-page">
			<div>
				<h1 className="page-title">Configurações</h1>
				<p className="page-subtitle">Gerencie os ajustes da conta.</p>
			</div>

			{visibleSections.map((section) => (
				<section key={section.title} className="settings-page__section">
					<div>
						<h2 className="settings-page__section-title">{section.title}</h2>
						<p className="settings-page__section-subtitle">
							{section.description}
						</p>
					</div>

					<div className="card-grid">
						{section.cards.map((card) => {
							const Icon = card.icon;
							return (
								<Link
									key={card.segment}
									to={`/${obra}/configuracoes/${card.segment}`}
									className="settings-card-link group"
								>
									<Card className="settings-card">
										<CardHeader>
											<div className="settings-card__top">
												<div className="settings-card__icon">
													<Icon />
												</div>
												<ArrowRight className="settings-card__arrow" />
											</div>
											<CardTitle className="settings-card__title">
												{card.title}
											</CardTitle>
											<CardDescription>{card.description}</CardDescription>
										</CardHeader>
									</Card>
								</Link>
							);
						})}
					</div>
				</section>
			))}
		</div>
	);
}

function SettingsCardSkeleton() {
	return (
		<Card className="settings-card">
			<CardHeader>
				<div className="settings-card__top">
					<Skeleton className="settings-card__skeleton-icon" />
				</div>
				<Skeleton className="settings-card__skeleton-title" />
				<Skeleton className="settings-card__skeleton-desc" />
			</CardHeader>
		</Card>
	);
}
